import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { School, Send, CheckCircle2, QrCode, NotebookPen, PackageCheck } from "lucide-react";

const PARTNER_PERKS = [
  {
    icon: PackageCheck,
    title: "Bulk License Bundles",
    desc: "Counsellors, career guidance professionals, schools and institutions can enrol whole batches at once with bulk assessment license packages — start with a bundle and top up any time as enrolment grows.",
  },
  {
    icon: QrCode,
    title: "Your Own Branded Link & QR Code",
    desc: "Every partner gets a personalised referral link and QR code carrying your name and institution, so every student who takes the assessment is tracked back to you.",
  },
  {
    icon: NotebookPen,
    title: "Private Counsellor Notes",
    desc: "Add your own private notes on any student's report — visible only to you — to capture context for your next counselling session.",
  },
];

export default function PartnerSection() {
  const [form, setForm] = useState({ name: "", organization: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        subject: "School / Career Counsellor Partnership Inquiry",
        message: `Organization: ${form.organization}\n\n${form.message}`,
      });
      setSent(true);
    } catch {
      toast.error("Could not send your message, please try again");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="partner" className="relative overflow-hidden bg-gradient-to-br from-[#0B0F1E] via-[#141C38] to-[#0B0F1E] py-20 sm:py-28 px-6" data-testid="partner-section">
      <div className="absolute -bottom-24 left-0 h-80 w-80 rounded-full bg-indigo-600/20 blur-[100px]" />
      <div className="max-w-5xl mx-auto relative grid lg:grid-cols-[1fr_1.1fr] gap-12 items-start">
        <div>
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-indigo-600 to-cyan-500 flex items-center justify-center mb-6">
            <School className="h-6 w-6 text-white" />
          </div>
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-cyan-400 mb-3">For Schools &amp; Career Counsellors</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-5">
            Bring CareerStudy360 to Your Students
          </h2>
          <p className="text-sm sm:text-base text-slate-400 leading-relaxed">
            We partner with schools, coaching institutes and independent career counsellors to give every student
            access to a whole-person, research-grounded career assessment — with results counsellors can act on
            directly. Tell us about your institution and we'll get in touch.
          </p>

          <div className="mt-8 space-y-5" data-testid="partner-perks-list">
            {PARTNER_PERKS.map((perk) => (
              <div key={perk.title} className="flex items-start gap-3" data-testid={`partner-perk-${perk.title.slice(0, 10)}`}>
                <div className="h-9 w-9 shrink-0 rounded-lg bg-white/5 border border-slate-700 flex items-center justify-center">
                  <perk.icon className="h-4 w-4 text-cyan-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{perk.title}</p>
                  <p className="text-xs text-slate-400 leading-relaxed mt-0.5">{perk.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 sm:p-8">
          {sent ? (
            <div className="text-center py-8" data-testid="partner-success-message">
              <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-4" />
              <p className="text-emerald-300 font-medium">Thanks for reaching out — our team will get back to you shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="partner-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Input
                  data-testid="partner-name-input" placeholder="Your name" required
                  value={form.name} onChange={(e) => update("name", e.target.value)}
                  className="bg-slate-950 border-slate-700 text-white"
                />
                <Input
                  data-testid="partner-organization-input" placeholder="School / Institution name" required
                  value={form.organization} onChange={(e) => update("organization", e.target.value)}
                  className="bg-slate-950 border-slate-700 text-white"
                />
              </div>
              <Input
                type="email" data-testid="partner-email-input" placeholder="Email address" required
                value={form.email} onChange={(e) => update("email", e.target.value)}
                className="bg-slate-950 border-slate-700 text-white"
              />
              <Textarea
                data-testid="partner-message-input" placeholder="Tell us about your students and what you're looking for"
                rows={4} required value={form.message} onChange={(e) => update("message", e.target.value)}
                className="bg-slate-950 border-slate-700 text-white"
              />
              <Button data-testid="partner-submit-button" disabled={sending} type="submit" className="w-full h-11 rounded-xl bg-indigo-600 hover:bg-indigo-500">
                <Send className="h-4 w-4 mr-2" /> {sending ? "Sending…" : "Get in Touch"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
